import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import Button from '../../../../common/src/components/Button'
import link, { Link } from '../../../../common/src/components/Link'
import Table from '../../../../common/src/components/Table'
import {
  EllipsisDiv,
  ColoredSpan,
  BodyText,
  Spacer,
  H4,
  AlignRight,
  BodyTextSpan,
} from '../../../../common/src/components/core'
import { lex, getState } from '../../../../common/src/func'
import {
  FetchPropertiesQuery,
  FetchPropertiesQueryVariables,
  FetchTasksQuery,
  FetchTasksQueryVariables,
  FetchUserContextQuery,
  TaskType,
} from '../../../../common/src/types.gen'
import { PollingQuery } from '../components/PollingQuery'
import { TasksTable } from '../components/TasksTable'

const fetchTasksQuery = gql`
  query FetchTasks($firmId: Int!, $fetchArchived: Boolean!) {
    firm(firmId: $firmId) {
      id
      tasks(includeArchived: $fetchArchived, populateArchived: false) {
        id
        displayName
        isArchived
        timeCreated
        launchDate
        offboardDate
        taskType
        leasingInfo {
          propertyType
        }
      }
    }
  }
`

const firmProperties = gql`
  fragment FirmProperties on Firm {
    id
    pmPropertiesWithoutTasks {
      id
      displayName
      auditKeyJson
      jsonUrl
      pmUrl
      numUnits
      numUnitsUnrented
      cleanAddress
    }
  }
`

const fetchPropertiesQuery = gql`
  query FetchProperties($firmId: Int!) {
    firm(firmId: $firmId) {
      ...FirmProperties
    }
  }
  ${firmProperties}
`

type PmProp = FetchPropertiesQuery['firm']['pmPropertiesWithoutTasks'][0]

type PropertiesTableImplProps = FetchPropertiesQueryVariables &
  QueryResult<FetchPropertiesQuery, FetchPropertiesQueryVariables>

interface PropertiesTableImplState {
  properties: PmProp[]
  sortedProps: PmProp[]
}

class PropertiesTableImpl extends React.PureComponent<PropertiesTableImplProps> {
  state = { properties: [], sortedProps: [] } as PropertiesTableImplState

  static getDerivedStateFromProps(props: PropertiesTableImplProps, state: PropertiesTableImplState) {
    const { data } = props
    if (data == null || data.firm == null || data.firm.pmPropertiesWithoutTasks == null) {
      return null
    }

    const properties = data.firm.pmPropertiesWithoutTasks
    if (properties === state.properties) {
      return null
    }

    const sortedProps = data.firm.pmPropertiesWithoutTasks.slice()
    sortedProps.sort(
      lex(
        (r: PmProp) => (r.cleanAddress ? (getState(r.cleanAddress) || 'zzz') + ' ' + r.cleanAddress : 'zzz'),
        (r: PmProp) => r.id
      )
    )
    return { sortedProps, properties }
  }

  render() {
    const { data } = this.props
    const { sortedProps } = this.state

    if (data == null || data.firm == null || data.firm.pmPropertiesWithoutTasks == null) {
      return null
    }

    return (
      <div>
        <Table
          rows={sortedProps}
          columns={this.cols}
          headers={this.headers}
          widths={this.widths}
          alignments={this.aligns}
          keyFn={this.keyFn}
        />
      </div>
    )
  }

  private keyFn = (r: PmProp) => r.id

  private static colAddr = (r: PmProp) => (
    <EllipsisDiv title={r.cleanAddress || undefined}>
      {getState(r.cleanAddress) || '–'} <ColoredSpan color="silver">{r.cleanAddress}</ColoredSpan>
    </EllipsisDiv>
  )

  private static colDisplayName = (r: PmProp) => (
    <EllipsisDiv title={r.displayName}>
      {r.auditKeyJson.includes('"APPFOLIO"') ? <Link href={r.pmUrl}>{r.displayName}</Link> : r.displayName}
    </EllipsisDiv>
  )

  private static colNumUnits = (r: PmProp) => '' + r.numUnits

  private cols = [
    PropertiesTableImpl.colAddr,
    PropertiesTableImpl.colDisplayName,
    PropertiesTableImpl.colNumUnits,
    /* PropertiesTableImpl._colUnrented,*/
  ]

  private headers = ['Address', 'Property', '# units' /* , 'Unrented'*/]

  private widths = [null, null, '44px' /* , '60px'*/]

  private aligns = ['left', 'left', 'right' /* , 'right'*/]
}

function PropertiesTable(props: FetchPropertiesQueryVariables) {
  return (
    <PollingQuery query={fetchPropertiesQuery} variables={props}>
      {(res: QueryResult<FetchPropertiesQuery, FetchPropertiesQueryVariables>) => (
        <PropertiesTableImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}

type Task = FetchTasksQuery['firm']['tasks'][0]

/**
 * Displays a list of currently active tasks under the given firm. Allows the user to bulk edit certain fields on the LeasingTask that represents each property.
 */
class FirmTasksTableImpl extends React.Component<
  FetchTasksQueryVariables & QueryResult<FetchTasksQuery, FetchTasksQueryVariables>
> {
  render() {
    const { data, fetchArchived } = this.props

    if (data == null || data.firm == null) {
      return null
    }

    const { tasks } = data.firm

    const filteredTasks = tasks
      .filter(task => task.taskType !== TaskType.LEASING_FIRM)
      .filter(task => fetchArchived === task.isArchived)

    // sort first by timeCreated, so that phased rollouts are together
    // then by taskType (MF, SFR, STUDENT, etc.)
    const sortedTasks = this.calcSortedTasks(filteredTasks)

    if (sortedTasks.length === 0) {
      return <BodyText>No tasks</BodyText>
    }

    return (
      <div>
        <TasksTable
          tasks={sortedTasks}
          as="Table"
          widths={['230px', null, null]}
          alignments={['left', 'right', 'right']}
        />
        <Spacer width={16} />
      </div>
    )
  }

  private calcSortedTasks(filteredTasks: FetchTasksQuery['firm']['tasks']) {
    return filteredTasks.slice().sort((a: Task, b: Task) => {
      const aDayCreated = a.timeCreated.substring(0, 10)
      const bDayCreated = b.timeCreated.substring(0, 10)
      let ret = bDayCreated.localeCompare(aDayCreated)
      if (a.leasingInfo && b.leasingInfo && a.leasingInfo.propertyType && b.leasingInfo.propertyType && ret === 0) {
        ret = a.leasingInfo.propertyType.localeCompare(b.leasingInfo.propertyType)
      }
      return ret
    })
  }
}

function FirmTasksTable(props: FetchTasksQueryVariables) {
  return (
    <PollingQuery query={fetchTasksQuery} variables={props} fetchPolicy="cache-and-network">
      {(res: QueryResult<FetchTasksQuery, FetchTasksQueryVariables>) => <FirmTasksTableImpl {...props} {...res} />}
    </PollingQuery>
  )
}

type TaskListTab = 'ACTIVE' | 'ARCHIVED' | 'PROPERTIES'

const LinkButton = link(Button)

export default class TaskListSection extends React.PureComponent<
  { firmId: number; user: FetchUserContextQuery['self'] },
  { tab: TaskListTab }
> {
  state = {
    tab: 'ACTIVE' as TaskListTab,
  }

  render() {
    const { firmId, user } = this.props
    const { tab } = this.state
    const canSeePropertiesWithoutTask = user.permissions.canViewSuperTaskDetails

    return (
      <div>
        <H4>Tasks</H4>
        <Spacer height={8} />
        <Button size="small" color="sky" filled={tab === 'ACTIVE'} onClick={this.setActive}>
          Active
        </Button>
        <Spacer width={8} />
        <Button size="small" color="sky" filled={tab === 'ARCHIVED'} onClick={this.setArchived}>
          Archived
        </Button>
        {canSeePropertiesWithoutTask && <Spacer width={8} />}
        {canSeePropertiesWithoutTask && (
          <Button size="small" color="sky" filled={tab === 'PROPERTIES'} onClick={this.setProps}>
            Properties without a task
          </Button>
        )}
        <Spacer height={8} />
        {['ACTIVE', 'ARCHIVED'].includes(tab) && <FirmTasksTable firmId={firmId} fetchArchived={tab === 'ARCHIVED'} />}
        {'PROPERTIES' === tab && <PropertiesTable firmId={firmId} />}
        <Spacer height={16} />
        <AlignRight>
          <BodyTextSpan>editing a few at once?</BodyTextSpan>
          <Spacer width={16} />
          <LinkButton to={`/leasing/tasks/bulk_update/firm/${firmId}`}>Bulk Update</LinkButton>
        </AlignRight>
        <Spacer height={32} />
      </div>
    )
  }

  private setActive = () => this.setState({ tab: 'ACTIVE' })
  private setArchived = () => this.setState({ tab: 'ARCHIVED' })
  private setProps = () => this.setState({ tab: 'PROPERTIES' })
}
